import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../ui/select';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { UserCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EDITOR_ROLES = ['admin', 'cs', 'cro', 'cco', 'super_admin'];
const UNASSIGNED = '__unassigned__';

/**
 * Sales Lead assignment for an estimate. Lists Sales-role reps and lets an
 * authorized user (creator + leadership) assign / change / clear the owner.
 *
 * - With `estimateId`: persists immediately via PUT /estimates/{id}/sales-lead
 *   (auditable, retroactive) and notifies the parent via onChange.
 * - Without `estimateId` (new-estimate wizard): updates parent state only;
 *   the value is sent in the create payload.
 */
export const SalesLeadSelector = ({ estimateId = null, value = null, onChange, disabled = false }) => {
  const { user } = useAuth();
  const canEdit = !disabled && EDITOR_ROLES.includes(user?.role);
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/estimates/assignable-sales-leads`, { withCredentials: true });
        if (!cancelled) setReps(res.data?.sales_leads || []);
      } catch (e) {
        // non-fatal — selector just shows current value
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSelect = useCallback(async (selected) => {
    const userId = selected === UNASSIGNED ? null : selected;
    const rep = userId ? reps.find((r) => r.user_id === userId) : null;
    const newLead = rep ? { user_id: rep.user_id, name: rep.name, email: rep.email } : null;

    // No estimate yet (wizard) — just bubble up; persisted on create.
    if (!estimateId) {
      onChange?.(newLead);
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_URL}/api/estimates/${estimateId}/sales-lead`,
        { user_id: userId },
        { withCredentials: true },
      );
      onChange?.(res.data?.sales_lead || null);
      toast.success(newLead ? `Sales Lead set to ${newLead.name}` : 'Sales Lead cleared');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update Sales Lead');
    } finally {
      setSaving(false);
    }
  }, [estimateId, reps, onChange]);

  const currentId = value?.user_id || UNASSIGNED;

  return (
    <Card className="border-slate-200" data-testid="sales-lead-card">
      <CardContent className="p-4">
        <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
          <UserCircle2 className="w-3.5 h-3.5 text-[#4ABFCF]" />
          Sales Lead
          {(saving || loading) && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
        </Label>
        <p className="text-[11px] text-slate-400 mb-2 mt-0.5">
          The Sales rep who owns this estimate. They&apos;ll see it in their estimate list. Changes are audited.
        </p>
        <Select value={currentId} onValueChange={handleSelect} disabled={!canEdit || saving || loading}>
          <SelectTrigger className="w-full sm:w-80" data-testid="sales-lead-select">
            <SelectValue placeholder="Unassigned" />
          </SelectTrigger>
          <SelectContent data-testid="sales-lead-select-content">
            <SelectItem value={UNASSIGNED} data-testid="sales-lead-option-unassigned">Unassigned</SelectItem>
            {/* Ensure the currently-assigned lead is always selectable even if
                they're no longer in the active Sales list. */}
            {value?.user_id && !reps.some((r) => r.user_id === value.user_id) && (
              <SelectItem value={value.user_id}>{value.name || value.email}</SelectItem>
            )}
            {reps.map((r) => (
              <SelectItem key={r.user_id} value={r.user_id} data-testid={`sales-lead-option-${r.user_id}`}>
                {r.name}{r.email ? ` · ${r.email}` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!canEdit && value?.name && (
          <p className="text-[11px] text-slate-500 mt-1.5" data-testid="sales-lead-readonly">
            Assigned to <span className="font-medium text-[#0F2040]">{value.name}</span>
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default SalesLeadSelector;
